import { useCallback, useEffect, useRef } from 'react';

import { usePlayerStore } from '../components/PlayerContextProvider';

export type UsePlayerCurrentTimeObserverParams = {
  interval: number;
  onTimeUpdate?: (currentTime: number) => void;
};

/**
 * This hook runs an interval every X ms to get the current time of the video
 * and calls the onTimeUpdate callback with the current time. There is an event
 * listener for the 'timeupdate' event on the video element, but it is called
 * every 50-250ms, which is not frequent enough for our use case.
 */
export const usePlayerCurrentTimeObserver = ({ interval, onTimeUpdate }: UsePlayerCurrentTimeObserverParams) => {
  const id = usePlayerStore((state) => state.id);
  const videoElement = usePlayerStore((state) => state.videoElement);
  const setCurrentTime = usePlayerStore((state) => state.setCurrentTime);
  const intervalRef = useRef<number | null>(null);
  const currentTimeRef = useRef<number | null>(null);

  const stopObservingCurrentTime = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);

      intervalRef.current = null;
    }
  }, []);

  const startObservingCurrentTime = useCallback(() => {
    stopObservingCurrentTime();

    intervalRef.current = window.setInterval(() => {
      if (!videoElement) {
        return;
      }

      const currentTime = videoElement.currentTime;

      if (currentTimeRef.current !== currentTime) {
        currentTimeRef.current = currentTime;
        setCurrentTime(currentTime);
        onTimeUpdate?.(currentTime);
      }
    }, interval);
  }, [interval, stopObservingCurrentTime, videoElement, onTimeUpdate, setCurrentTime]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(id): We need to stop observing current time when the player id changes
  useEffect(() => {
    return () => {
      stopObservingCurrentTime();
    };
  }, [id, stopObservingCurrentTime]);

  return {
    startObservingCurrentTime,
    stopObservingCurrentTime,
  };
};
